import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import abiVestingHMC from 'abi/abi-vesting-hmc.json'
import { AppAdmin } from 'api/app-admin'
import { VestingTypeEnum } from 'constants/vesting'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import { useContractManager } from 'hooks/use-contract-manager'
import { useVesting } from 'hooks/vesting/use-vesting'
import { logError } from 'utils/logger'

interface VestingContextType {
  scVesting: string
  loading: boolean
  loadingDataContract: boolean
  contract: any
  isContractManager: boolean
  totalUser: number
  totalToken: number
  totalClaim?: number
  type?: VestingTypeEnum
  setScVesting: (address: string | undefined) => void
  checkContractManager: () => Promise<void>
  [key: string]: any
}

const VestingContext = createContext<VestingContextType>({
  scVesting: '',
  loading: false,
  loadingDataContract: false,
  contract: null,
  isContractManager: false,
  totalUser: 0,
  totalToken: 0,
  totalClaim: 0,
  setScVesting: () => {},
  checkContractManager: async () => {},
})

interface VestingContextProps {
  type: VestingTypeEnum
  children: ReactNode
}
const VestingProvider: React.FC<VestingContextProps> = ({ type, children }) => {
  const [scVesting, setScVesting]: any = useState('')

  const [loading, setLoading] = useState(false)

  const [totalUser, setTotalUser] = useState(0)

  const [totalToken, setTotalToken] = useState(0)

  const [totalClaim, setTotalClaim] = useState(0)

  const contract: any = useContractViem(scVesting, abiVestingHMC)

  const { loading: loadingDataContract, ...resultDataContract } = useVesting({
    contract,
    contractAddress: scVesting,
  })

  const { isContractManager, checkContractManager } = useContractManager({ contract })

  const getVestingStatistical = useCallback(async () => {
    setLoading(true)
    try {
      const res = await AppAdmin.getVestingStatistical(type)
      if (res?.data.success) {
        const data = res.data.data || {}
        setScVesting(data.contractAddress)
        setTotalUser(data?.totalUser)
        setTotalToken(data?.totalToken)
        setTotalClaim(data?.totalClaim)
      }
    } catch (error) {
      logError('Error fetching data: ', error)
    } finally {
      setLoading(false)
    }
  }, [type])

  useEffect(() => {
    getVestingStatistical()
  }, [getVestingStatistical])

  return (
    <VestingContext.Provider
      value={{
        ...resultDataContract,
        type,
        scVesting: scVesting,
        isContractManager,
        loading,
        loadingDataContract,
        contract: contract,
        totalUser: totalUser,
        totalToken: totalToken,
        totalClaim: totalClaim,
        checkContractManager,
        setScVesting,
      }}
    >
      {children}
    </VestingContext.Provider>
  )
}

const useVestingContext = (): VestingContextType => {
  const context = useContext(VestingContext)

  if (context === undefined) {
    throw new Error('Context not available')
  }

  return context
}

export { useVestingContext, VestingProvider }
